/**
 * The contest's own copy, drawn the way a player will meet it.
 *
 * Two screens show this tile and the ticket asks that they "show the same
 * thing": the Contest Overview's read-only Player preview card (CAE-1546,
 * `.preview-*` in `prototype/contest-overview.css`) and Edit › Basic Info's
 * live preview, which re-renders on every keystroke (CAE-1549, `.bi-preview-card`
 * in `prototype/modals.css`). They are the same tile at two scales, so they are
 * one component — two implementations agreeing today is not the same promise.
 *
 * The tile is **the one intentional gradient in the app**. design-system.md §7
 * forbids gradient backgrounds and then names this as the single exception,
 * because the tile is not admin chrome — it is a simulation of the player app,
 * and it wears that app's wash and ink rather than the §1 ramp. Its palette
 * lives in `componentColor.previewTile` for the same reason.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

/**
 * The two scales the handoff draws, as the stylesheets have them.
 *
 * Type and metrics only — every colour comes from `componentColor.previewTile`,
 * which both sizes share. The name starts from `text.h2` in both: that token is
 * already 22/700 at `-0.015em`, which is `.preview-name` exactly, and
 * `.bi-preview-name` is the same face one step down. So only the size and the
 * leading are stated here, and `-0.015em` stays in one place.
 */
const SCALE = {
  // `.preview-card` / `.preview-*` — contest-overview.css.
  md: {
    radius: 'insetTile',
    padding: '22px 24px',
    superSize: '11px',
    superGap: '10px',
    nameGap: '6px',
    nameSize: undefined,
    nameLineHeight: 1.15,
    subSize: '14px',
    subWeight: 600,
    subGap: '8px',
    descriptionSize: '13.5px',
  },
  // `.bi-preview-card` / `.bi-preview-*` — modals.css.
  sm: {
    radius: 'insetRow',
    padding: '14px 16px',
    superSize: '10.5px',
    superGap: '6px',
    nameGap: '4px',
    nameSize: '18px',
    nameLineHeight: 1.2,
    subSize: '13px',
    subWeight: 500,
    subGap: '6px',
    descriptionSize: '12.5px',
  },
} as const;

export type PlayerPreviewTileSize = keyof typeof SCALE;

interface PlayerPreviewTileProps {
  /** The contest's name. Empty renders `namePlaceholder`, or nothing. */
  name: string;
  superTitle: string | null;
  subTitle: string | null;
  description: string | null;
  size?: PlayerPreviewTileSize;
  /**
   * Stands in for an empty name, italic in `--ink-4` — the live preview's
   * "Contest name…". The Overview has no use for it: a saved contest always has
   * a name, and a placeholder there would preview a state that cannot exist.
   */
  namePlaceholder?: string;
}

/**
 * A line the contest has not been given.
 *
 * Whitespace counts as unset: an operator who clears the field in the Edit form
 * leaves `"  "` behind as readily as `null`, and a player sees neither. All
 * three optional lines are held to it, because a reserved empty paragraph
 * previews a layout the player app never renders.
 */
const copyLine = (value: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

export const PlayerPreviewTile = ({
  name,
  superTitle,
  subTitle,
  description,
  size = 'md',
  namePlaceholder,
}: PlayerPreviewTileProps) => {
  const scale = SCALE[size];
  const superLine = copyLine(superTitle);
  const subLine = copyLine(subTitle);
  const descriptionLine = copyLine(description);
  const nameLine = copyLine(name);

  return (
    <Box
      sx={(theme: Theme) => ({
        background: theme.tokens.componentColor.previewTile.gradient,
        border: `${theme.tokens.border.width} solid ${theme.tokens.componentColor.previewTile.border}`,
        borderRadius: `${theme.tokens.radius[scale.radius]}px`,
        padding: scale.padding,
      })}
    >
      {superLine ? (
        <Box
          component="span"
          sx={(theme: Theme) => ({
            display: 'block',
            marginBottom: scale.superGap,
            fontSize: scale.superSize,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: theme.tokens.componentColor.previewTile.super,
          })}
        >
          {superLine}
        </Box>
      ) : null}

      {/*
        The contest's name as a player reads it, which is the whole point of the
        tile — so a heading. `h3` because both hosts put it under an `h2`: the
        Overview card's title, and the Edit section's "Display". The tag only has
        to keep the document outline descending (axe `heading-order`); the size
        comes from the scale.

        Dropped entirely when there is neither a name nor a placeholder to stand
        in for one, rather than rendered empty — an `<h3></h3>` is an axe
        `empty-heading` failure and announces a heading with nothing in it. In
        practice that is the Overview's path on a nameless contest: the Edit form
        always passes a placeholder, so its heading never goes away and the tile
        does not change shape under an operator mid-keystroke.
      */}
      {(nameLine ?? namePlaceholder) ? (
        <Box
          component="h3"
          sx={(theme: Theme) => ({
            margin: `0 0 ${scale.nameGap}`,
            ...theme.tokens.text.h2,
            ...(scale.nameSize ? { fontSize: scale.nameSize } : {}),
            lineHeight: scale.nameLineHeight,
            color: theme.tokens.componentColor.previewTile.name,
          })}
        >
          {nameLine ?? (
            /*
              Italic and in `--ink-4` so it reads as absence rather than as a
              contest called "Contest name…".
            */
            <Box component="em" sx={(theme: Theme) => ({ color: theme.tokens.color.ink4 })}>
              {namePlaceholder}
            </Box>
          )}
        </Box>
      ) : null}

      {subLine ? (
        <Box
          component="p"
          sx={(theme: Theme) => ({
            margin: `0 0 ${scale.subGap}`,
            fontSize: scale.subSize,
            fontWeight: scale.subWeight,
            color: theme.tokens.componentColor.previewTile.sub,
          })}
        >
          {subLine}
        </Box>
      ) : null}

      {descriptionLine ? (
        <Box
          component="p"
          sx={(theme: Theme) => ({
            margin: 0,
            fontSize: scale.descriptionSize,
            lineHeight: 1.45,
            color: theme.tokens.componentColor.previewTile.description,
          })}
        >
          {descriptionLine}
        </Box>
      ) : null}
    </Box>
  );
};
